import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { ControlCenterTotals } from "@/features/admin";
import { useChartTheme } from "@/features/charts/theme";
import { ChartTooltip } from "@/features/charts/tooltip";
import { ResponsivePie } from "@nivo/pie";
import { percent, RESULT_LABEL } from "./format";

/**
 * How conversations ended (the wrap-up's own verdict) and how the caller felt about it.
 * Only closed conversations are counted: an open one has no result yet.
 */
export function Outcomes({ totals }: { totals: ControlCenterTotals }) {
  const { theme, tokens } = useChartTheme();

  const results = [
    { id: "success", value: totals.success, color: tokens.tone("green") },
    { id: "failure", value: totals.failure, color: tokens.tone("red") },
    { id: "no_answer", value: totals.no_answer, color: tokens.tone("yellow") },
    { id: "voicemail", value: totals.voicemail, color: tokens.series[1] },
    { id: "unknown", value: totals.unknown, color: tokens.tone("gray") },
  ]
    .filter((r) => r.value > 0)
    .map((r) => ({ ...r, label: RESULT_LABEL[r.id] }));

  const rated = totals.success + totals.failure;
  const sentiments = [
    { key: "positive", label: "Positif", value: totals.positive, cls: "bg-success" },
    { key: "neutral", label: "Neutre", value: totals.neutral, cls: "bg-muted-foreground/40" },
    { key: "negative", label: "Négatif", value: totals.negative, cls: "bg-danger" },
  ];
  const sentimentTotal = sentiments.reduce((s, x) => s + x.value, 0);

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Est-ce que ça s'est bien passé ?</CardTitle>
        <p className="text-xs text-muted-foreground">
          {totals.closed} conversation(s) terminée(s)
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {results.length ? (
          <div className="flex items-center gap-4">
            <div className="relative h-[170px] w-[170px] shrink-0">
              <ResponsivePie
                data={results}
                colors={{ datum: "data.color" }}
                margin={{ top: 6, right: 6, bottom: 6, left: 6 }}
                innerRadius={0.74}
                padAngle={2}
                cornerRadius={6}
                activeOuterRadiusOffset={5}
                enableArcLabels={false}
                enableArcLinkLabels={false}
                theme={theme}
                motionConfig="gentle"
                tooltip={({ datum }) => (
                  <ChartTooltip
                    title={String(datum.label)}
                    rows={[
                      {
                        color: datum.color,
                        label: "Conversations",
                        value: `${datum.value} · ${percent(datum.value, totals.closed)}`,
                      },
                    ]}
                  />
                )}
              />
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold tabular-nums">
                  {percent(totals.success, rated)}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  de réussite
                </span>
              </div>
            </div>
            <ul className="min-w-0 flex-1 space-y-1.5 text-xs">
              {results.map((r) => (
                <li key={r.id} className="flex items-center gap-2">
                  <span
                    className="size-2 shrink-0 rounded-full"
                    style={{ background: r.color }}
                  />
                  <span className="truncate text-muted-foreground">
                    {r.label}
                  </span>
                  <span className="ml-auto tabular-nums font-medium">
                    {r.value}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="flex h-[170px] items-center justify-center text-sm text-muted-foreground">
            Aucune conversation terminée.
          </div>
        )}

        <div>
          <div className="mb-1.5 flex items-center justify-between text-xs">
            <span className="font-medium">Ressenti client</span>
            <span className="text-muted-foreground">
              😊 {percent(totals.positive, sentimentTotal)} · 😟{" "}
              {percent(totals.negative, sentimentTotal)}
            </span>
          </div>
          <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
            {sentimentTotal
              ? sentiments.map((s) =>
                  s.value ? (
                    <div
                      key={s.key}
                      className={`${s.cls} h-full transition-all duration-700`}
                      style={{ width: `${(s.value / sentimentTotal) * 100}%` }}
                      title={`${s.label} : ${s.value}`}
                    />
                  ) : null,
                )
              : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
